import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { ApiError, apiFetch } from "../api/client";
import { authEntryPathForError } from "../lib/authRedirect";

type Member = { user_id: string; email: string; role: string };
type PendingInvitation = {
  id: string;
  email: string;
  role: string;
  expires_at: string;
};
type FolderEvent = {
  id: string;
  payload: { kind?: string; email?: string; role?: string; source?: string; selfLeave?: boolean };
  created_at: string;
};

function eventLabel(ev: FolderEvent): string {
  const k = ev.payload.kind;
  const when = new Date(ev.created_at).toLocaleString("ru-RU");
  if (k === "member_added") {
    const src = ev.payload.source === "invitation_accept" ? " (приглашение)" : "";
    return `${when} · добавлен ${ev.payload.email} · ${ev.payload.role}${src}`;
  }
  if (k === "invitation_sent") {
    return `${when} · приглашение → ${ev.payload.email} · ${ev.payload.role}`;
  }
  if (k === "member_removed") {
    return `${when} · участник удалён${ev.payload.selfLeave ? " (сам вышел)" : ""}`;
  }
  return `${when} · ${k ?? "событие"}`;
}

export function FolderSettingsPage() {
  const { id: folderId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [sub, setSub] = useState("Загрузка…");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [myUserId, setMyUserId] = useState("");
  const [members, setMembers] = useState<Member[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [memberEmail, setMemberEmail] = useState("");
  const [memberRole, setMemberRole] = useState("editor");
  const [events, setEvents] = useState<FolderEvent[]>([]);

  useEffect(() => {
    if (!folderId) {
      navigate("/cabinet", { replace: true });
      return;
    }
    void (async () => {
      try {
        const [me, membersData] = await Promise.all([
          apiFetch<{ id: string }>("/api/v1/me"),
          apiFetch<{
            folder?: { id: string; name: string };
            owner: { user_id: string; email: string };
            members: Member[];
            pending_invitations?: PendingInvitation[];
            is_owner?: boolean;
          }>(`/api/v1/folders/${folderId}/members`),
        ]);
        setMyUserId(me.id);
        const ownerMode = membersData.is_owner === true || membersData.owner.user_id === me.id;
        setIsOwner(ownerMode);
        setIsMember(!ownerMode && membersData.members.some((m) => m.user_id === me.id));
        setOwnerEmail(membersData.owner.email);
        setMembers(membersData.members || []);
        setPendingInvitations(membersData.pending_invitations || []);
        if (membersData.folder?.name) setName(membersData.folder.name);
        setSub(
          ownerMode
            ? `ID ${folderId} · вы владелец · доступ наследуется на подпапки и доски`
            : "Участник папки · доступ ко всем доскам внутри"
        );

        if (ownerMode) {
          const ev = await apiFetch<{ events: FolderEvent[] }>(
            `/api/v1/folders/${folderId}/events?limit=30`
          );
          setEvents(ev.events || []);
        }
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          navigate(authEntryPathForError(e, location.pathname), { replace: true });
          return;
        }
        setErr(e instanceof Error ? e.message : "Ошибка загрузки");
        setSub("");
      }
    })();
  }, [folderId, navigate]);

  async function reloadMembers() {
    if (!folderId) return;
    const m = await apiFetch<{
      members: Member[];
      pending_invitations?: PendingInvitation[];
    }>(`/api/v1/folders/${folderId}/members`);
    setMembers(m.members || []);
    setPendingInvitations(m.pending_invitations || []);
    if (isOwner) {
      const ev = await apiFetch<{ events: FolderEvent[] }>(
        `/api/v1/folders/${folderId}/events?limit=30`
      );
      setEvents(ev.events || []);
    }
  }

  async function addMember(e: FormEvent) {
    e.preventDefault();
    if (!folderId) return;
    try {
      const res = await apiFetch<{
        member?: Member;
        invitation?: PendingInvitation & { mail_sent?: boolean };
      }>(`/api/v1/folders/${folderId}/members`, {
        method: "POST",
        json: { email: memberEmail.trim(), role: memberRole },
      });
      setMemberEmail("");
      if (res.invitation) {
        setOk(
          res.invitation.mail_sent
            ? "Приглашение отправлено на email"
            : "Приглашение создано (SMTP не настроен — передайте ссылку вручную)"
        );
      } else {
        setOk("Участник добавлен");
      }
      await reloadMembers();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    }
  }

  async function leaveFolder() {
    if (!folderId || !myUserId) return;
    if (!confirm("Покинуть папку? Доступ ко всем доскам внутри будет потерян.")) return;
    try {
      await apiFetch(`/api/v1/folders/${folderId}/members/${myUserId}`, { method: "DELETE" });
      navigate("/cabinet?section=shared", { replace: true });
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    }
  }

  return (
    <>
      <SiteHeader
        right={
          <>
            <Link className="wb-btn wb-btn--ghost wb-btn--sm" to="/cabinet">
              Кабинет
            </Link>
            {folderId ? (
              <Link
                className="wb-btn wb-btn--ghost wb-btn--sm"
                to={`/cabinet?folder=${folderId}${isOwner ? "" : "&section=shared"}`}
              >
                К папке
              </Link>
            ) : null}
          </>
        }
      />
      <div className="wb-shell wb-shell--narrow">
        {err ? <p className="wb-alert wb-alert--error">{err}</p> : null}
        {ok ? <p className="wb-alert wb-alert--ok">{ok}</p> : null}
        <h1 className="wb-h1">Доступ к папке: {name || "…"}</h1>
        <p className="wb-muted">{sub}</p>

        <section className="wb-panel wb-settings-block">
          <h2 className="wb-h2">Участники</h2>
          <p className="wb-settings-hint">
            Участники папки получают доступ ко всем доскам и подпапкам внутри. Роль «просмотр»
            ограничивает редактирование даже при прямом доступе к доске.
          </p>
          <ul className="wb-settings-list">
            <li className="wb-settings-list__item">
              <span>
                <strong>{ownerEmail}</strong> · владелец
              </span>
            </li>
            {members.map((m) => (
              <li key={m.user_id} className="wb-settings-list__item">
                <span>{m.email}</span>
                {isOwner ? (
                  <div className="wb-settings-list__actions">
                    <select
                      className="wb-select wb-select--sm"
                      value={m.role}
                      aria-label={`Роль ${m.email}`}
                      onChange={(e) => {
                        const role = e.target.value;
                        void apiFetch(`/api/v1/folders/${folderId}/members/${m.user_id}`, {
                          method: "PATCH",
                          json: { role },
                        })
                          .then(() => {
                            setOk("Роль обновлена");
                            void reloadMembers();
                          })
                          .catch((ex) =>
                            setErr(ex instanceof Error ? ex.message : "Ошибка")
                          );
                      }}
                    >
                      <option value="editor">Редактор</option>
                      <option value="viewer">Только просмотр</option>
                    </select>
                    <button
                      type="button"
                      className="wb-btn wb-btn--danger wb-btn--sm"
                      onClick={() => {
                        if (!confirm(`Удалить ${m.email}?`)) return;
                        void apiFetch(`/api/v1/folders/${folderId}/members/${m.user_id}`, {
                          method: "DELETE",
                        }).then(async () => {
                          setOk("Участник удалён");
                          await reloadMembers();
                        });
                      }}
                    >
                      Удалить
                    </button>
                  </div>
                ) : (
                  <span className="wb-muted">{m.role}</span>
                )}
              </li>
            ))}
            {pendingInvitations.map((inv) => (
              <li key={inv.id} className="wb-settings-list__item">
                <span>
                  {inv.email} · <span className="wb-muted">ожидает ({inv.role})</span>
                </span>
                {isOwner ? (
                  <button
                    type="button"
                    className="wb-btn wb-btn--danger wb-btn--sm"
                    onClick={() => {
                      void apiFetch(`/api/v1/folders/${folderId}/invitations/${inv.id}`, {
                        method: "DELETE",
                      }).then(async () => {
                        setOk("Приглашение отозвано");
                        await reloadMembers();
                      });
                    }}
                  >
                    Отозвать
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          {isOwner ? (
            <form className="wb-form-stack wb-settings-form" onSubmit={addMember}>
              <div>
                <label className="wb-label" htmlFor="folder-member-email">
                  Email участника
                </label>
                <input
                  id="folder-member-email"
                  className="wb-input"
                  type="email"
                  required
                  value={memberEmail}
                  onChange={(e) => setMemberEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="wb-label" htmlFor="folder-member-role">
                  Роль
                </label>
                <select
                  id="folder-member-role"
                  className="wb-select"
                  value={memberRole}
                  onChange={(e) => setMemberRole(e.target.value)}
                >
                  <option value="editor">Редактор</option>
                  <option value="viewer">Только просмотр</option>
                </select>
              </div>
              <button type="submit" className="wb-btn wb-btn--primary">
                Добавить участника
              </button>
            </form>
          ) : null}
        </section>

        {isOwner && events.length > 0 ? (
          <section className="wb-panel wb-settings-block">
            <h2 className="wb-h2">История доступа</h2>
            <ul className="wb-settings-list">
              {events.map((ev) => (
                <li key={ev.id} className="wb-settings-list__item">
                  <span className="wb-muted">{eventLabel(ev)}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {isMember && !isOwner ? (
          <section className="wb-panel wb-settings-block">
            <h2 className="wb-h2">Покинуть папку</h2>
            <p className="wb-settings-hint">
              Вы перестанете видеть доски этой папки в кабинете «Со мной».
            </p>
            <button type="button" className="wb-btn wb-btn--danger" onClick={() => void leaveFolder()}>
              Покинуть папку
            </button>
          </section>
        ) : null}
      </div>
    </>
  );
}
